import React from "react";
import { Area, AreaChart, ResponsiveContainer } from "recharts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowUp, faArrowDown, faMinus } from "@fortawesome/free-solid-svg-icons";
import { JetSportBox } from "../../../components/BoxView/JetSportBox";
import { KpiGroupDto } from "../../../../data/models/responses/StatisticsResponses";

interface Props {
  label: string;
  group: KpiGroupDto | undefined;
  isLoading: boolean;
  // Tailwind color class for the sparkline + delta accent
  accentClass?: string;
  accentHex?: string;
  delayMs?: number;
}

export function KpiCard({
  label,
  group,
  isLoading,
  accentClass = "text-primary-100",
  accentHex = "#1B76DF",
  delayMs = 0,
}: Props) {
  const total = group?.total ?? 0;
  const delta = group?.deltaPercent ?? 0;
  const series = group?.dailySeries ?? [];

  const direction = delta > 0 ? "up" : delta < 0 ? "down" : "flat";
  const deltaIcon =
    direction === "up" ? faArrowUp : direction === "down" ? faArrowDown : faMinus;
  const deltaColor =
    direction === "up"
      ? "text-status-success"
      : direction === "down"
      ? "text-status-failed-1"
      : "text-gray-600";

  // Inject a unique gradient id per card so colors don't bleed across instances
  const gradientId = React.useId().replace(/:/g, "");

  return (
    <JetSportBox
      className="group relative overflow-hidden opacity-0 animate-fadeIn transition-shadow duration-300 hover:shadow-md"
      style={{
        animationDelay: `${delayMs}ms`,
        animationFillMode: "forwards",
      }}
    >
      <div className="px-6 pt-6 pb-4 flex flex-col gap-4 min-h-[180px]">
        {/* Label */}
        <div className="flex items-start justify-between">
          <span className="text-xxs uppercase tracking-[0.22em] text-gray-600 font-semibold">
            {label}
          </span>
          <span
            className={`inline-flex items-center gap-1.5 text-xs font-semibold ${deltaColor}`}
          >
            <FontAwesomeIcon icon={deltaIcon} className="text-[9px]" />
            {Math.abs(delta).toFixed(1)}%
          </span>
        </div>

        {/* Big number */}
        <div className="flex items-end gap-3">
          {isLoading ? (
            <div className="h-12 w-24 rounded bg-gray-100 animate-pulse" />
          ) : (
            <span className="text-5xl font-bold tracking-tight leading-none text-black tabular-nums">
              {total.toLocaleString()}
            </span>
          )}
        </div>

        {/* Sub-label: previous total */}
        <div className="text-xs text-gray-600">
          {!isLoading && (
            <span className="tabular-nums">
              vs <span className="font-medium">{(group?.previousTotal ?? 0).toLocaleString()}</span>{" "}
              previous period
            </span>
          )}
        </div>
      </div>

      {/* Sparkline footer */}
      <div className="h-12 w-full">
        {!isLoading && series.length > 0 && (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={series}
              margin={{ top: 4, right: 0, bottom: 0, left: 0 }}
            >
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={accentHex} stopOpacity={0.45} />
                  <stop offset="100%" stopColor={accentHex} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="count"
                stroke={accentHex}
                strokeWidth={2}
                strokeLinecap="round"
                fill={`url(#${gradientId})`}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </JetSportBox>
  );
}
